// RecetasPacienteUI.js - Rediseño Moderno Completo
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Avatar,
  Stack,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Modal,
} from '@mui/material';
import {
  Add as AddIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  LocalPharmacy as LocalPharmacyIcon,
  CalendarToday,
  Pets,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  MedicalServices as MedicalIcon,
  Scale as ScaleIcon,
  Thermostat as ThermostatIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  InfoOutlined as InfoOutlinedIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HistoryOutlined as HistoryIcon,
  FiberNew as FiberNewIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Vademecum from './Vademecum';
import { useRecetasPacienteLogic } from './RecetasPacienteLogic';
import LineNumberedTextarea from './LineNumberedTextarea';

// ─── Paleta y tokens de diseño ────────────────────────────────────────────────
const THEME = {
  primary: '#1a6b4a',
  primaryLight: '#e8f5ee',
  primaryMid: '#2e8b62',
  accent: '#f0a500',
  accentLight: '#fff8e6',
  danger: '#d64045',
  dangerLight: '#fdecea',
  surface: '#ffffff',
  surfaceAlt: '#f7f9f8',
  border: '#e0ebe6',
  borderDark: '#c5d9d0',
  textPrimary: '#0f2d1e',
  textSecondary: '#5a7a6e',
  textMuted: '#93b0a5',
  shadow: '0 2px 12px rgba(26,107,74,0.08)',
  shadowMd: '0 4px 24px rgba(26,107,74,0.12)',
  shadowLg: '0 8px 40px rgba(26,107,74,0.16)',
};

// ─── Subcomponentes de apoyo ──────────────────────────────────────────────────

const InfoField = ({ label, value, icon: Icon, editing, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: THEME.textMuted }} />}
      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: THEME.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {label}
      </Typography>
    </Box>
    {editing ? (
      children
    ) : (
      <Typography sx={{ fontSize: '14px', color: value ? THEME.textPrimary : THEME.textMuted, fontStyle: value ? 'normal' : 'italic' }}>
        {value || 'N/A'}
      </Typography>
    )}
  </Box>
);

const RecetaCard = ({ receta, selected, onClick, truncateText }) => {
  const fecha = receta.fecha_receta ? dayjs(receta.fecha_receta).format('DD MMM YYYY') : 'Sin fecha';
  const preview = truncateText(receta.detalle_receta, 55);
  const isNew = receta.id_receta && dayjs().diff(dayjs(receta.createdAt), 'day') < 2;

  return (
    <Box
      onClick={onClick}
      sx={{
        p: '12px 14px',
        borderRadius: '10px',
        cursor: 'pointer',
        border: `1.5px solid ${selected ? THEME.primary : THEME.border}`,
        bgcolor: selected ? THEME.primaryLight : THEME.surface,
        transition: 'all 0.18s ease',
        position: 'relative',
        '&:hover': {
          borderColor: THEME.primaryMid,
          bgcolor: selected ? THEME.primaryLight : '#f0f7f4',
          transform: 'translateX(2px)',
        },
      }}
    >
      {isNew && (
        <Chip
          label="Nueva"
          size="small"
          icon={<FiberNewIcon sx={{ fontSize: '12px !important' }} />}
          sx={{
            position: 'absolute', top: 8, right: 8,
            height: 18, fontSize: '10px', fontWeight: 700,
            bgcolor: THEME.accent, color: '#fff',
            '& .MuiChip-icon': { color: '#fff' },
          }}
        />
      )}
      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: selected ? THEME.primary : THEME.textPrimary, mb: 0.4 }}>
        {fecha}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: THEME.textSecondary, lineHeight: 1.4 }}>
        {preview}
      </Typography>
      {selected && (
        <Box sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: 3, height: '60%', bgcolor: THEME.primary, borderRadius: '0 2px 2px 0' }} />
      )}
    </Box>
  );
};



const ActionBar = ({ isEditing, selectedReceta, onEdit, onSave, onCancelEdit, onSaveNew, onCancelNew, onPrint, onDelete }) => {
  const isNewMode = isEditing && !selectedReceta;
  const isEditMode = isEditing && selectedReceta;
  const isViewMode = !isEditing && selectedReceta;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1,
      p: '10px 16px',
      borderTop: `1px solid ${THEME.border}`,
      bgcolor: THEME.surfaceAlt,
      borderRadius: '0 0 14px 14px',
    }}>
      {isViewMode && (
        <>
          <Button
            size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
            onClick={onDelete}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', borderColor: THEME.dangerLight, color: THEME.danger, '&:hover': { bgcolor: THEME.dangerLight } }}
          >
            Eliminar
          </Button>
          <Button
            size="small" variant="outlined" startIcon={<PrintIcon />}
            onClick={onPrint}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', borderColor: THEME.borderDark, color: THEME.textSecondary }}
          >
            Abrir PDF
          </Button>
          <Button
            size="small" variant="contained" startIcon={<EditIcon />}
            onClick={onEdit}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
          >
            Editar
          </Button>
        </>
      )}
      {isEditMode && (
        <>
          <Button
            size="small" variant="text"
            onClick={onCancelEdit}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', color: THEME.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            size="small" variant="contained" startIcon={<SaveIcon />}
            onClick={onSave}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
          >
            Guardar Cambios
          </Button>
        </>
      )}
      {isNewMode && (
        <>
          <Button
            size="small" variant="text"
            onClick={onCancelNew}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', color: THEME.textSecondary }}
          >
            Cancelar
          </Button>
          <Button
            size="small" variant="contained" startIcon={<CheckCircleOutlineIcon />}
            onClick={onSaveNew}
            sx={{ borderRadius: '8px', fontSize: '12px', textTransform: 'none', bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
          >
            Guardar Receta
          </Button>
        </>
      )}
    </Box>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const RecetasPacienteUI = ({ visible, onClose, id_paciente, id_cita }) => {
  const {
    formData, setFormData,
    recetas, selectedReceta,
    loading, error, errors,
    isEditing, setIsEditing,
    snackbar,
    isMaximized, setIsMaximized,
    isVademecumOpen, setIsVademecumOpen,
    clienteNombre, pacienteNombre, razaNombre, usuarioNombre, pacienteEdad,
    handleSaveReceta, handleEditReceta, handleDeleteReceta,
    handleSelectReceta, handleNewReceta, handleCancelNewReceta,
    handleCloseSnackbar, truncateText, countWords, countLines,
    fetchRecetas, handlePrintReceta,
  } = useRecetasPacienteLogic({ id_paciente, id_cita });

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('1');

  const plantillas = [
    { id: '1', nombre: 'Estándar', descripcion: 'Formato completo con columnas para paciente y detalles.' },
    { id: '2', nombre: 'Simple', descripcion: 'Versión minimalista para impresiones rápidas.' },
    { id: '3', nombre: 'Detallada', descripcion: 'Incluye espacio para firma y notas adicionales.' },
  ];

  const handleClose = () => {
    setIsMaximized(false);
    setIsEditing(false);
    onClose();
  };

  const tooltipFormato = (
    <Box sx={{ p: 0.5, maxWidth: 240 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, color: '#fff' }}>📝 Formato de texto</Typography>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 0.5 }} />
      {[
        { syntax: '_texto_', desc: 'Subrayado', ex: '_La vitamina D ayuda_' },
        { syntax: '*texto*', desc: 'Negrita', ex: '*Dieta cachorro*' },
        { syntax: '- item', desc: 'Viñeta', ex: '- Agua: nutriente clave' },
      ].map(({ syntax, desc, ex }) => (
        <Box key={syntax} sx={{ mb: 0.8 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontFamily: 'monospace', bgcolor: 'rgba(255,255,255,0.15)', px: 0.6, borderRadius: 0.5, fontSize: '11px', color: '#fff' }}>{syntax}</Typography>
            <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.75)' }}>→ {desc}</Typography>
          </Box>
          <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mt: 0.2, ml: 0.5, fontStyle: 'italic' }}>{ex}</Typography>
        </Box>
      ))}
    </Box>
  );

  if (!visible) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 8, gap: 2 }}>
      <CircularProgress size={48} sx={{ color: THEME.primary }} />
      <Typography sx={{ color: THEME.textSecondary, fontSize: '14px' }}>Cargando recetas...</Typography>
    </Box>
  );
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;

  const showDetail = selectedReceta || isEditing;

  return (
    <Box
      sx={{
        bgcolor: THEME.surfaceAlt,
        borderRadius: isMaximized ? 0 : '16px',
        overflow: 'hidden',
        position: isMaximized ? 'fixed' : 'relative',
        top: 0, left: 0, right: 0, bottom: 0,
        width: isMaximized ? '100vw' : '100%',
        height: isMaximized ? '100vh' : 'auto',
        zIndex: isMaximized ? 800 : 'auto',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isMaximized ? 'none' : THEME.shadowLg,
        border: `1px solid ${THEME.border}`,
        fontFamily: '"DM Sans", "Segoe UI", sans-serif',
      }}
    >
      {/* ── CABECERA ─────────────────────────────────────────────────── */}
      <Box sx={{
        px: 3, py: 2,
        bgcolor: THEME.surface,
        borderBottom: `1px solid ${THEME.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexShrink: 0,
      }}>
        {/* Info paciente */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{
            width: 48, height: 48,
            bgcolor: THEME.primaryLight,
            border: `2px solid ${THEME.primary}`,
          }}>
            <Pets sx={{ color: THEME.primary, fontSize: 24 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: THEME.textPrimary, lineHeight: 1.2 }}>
              {pacienteNombre}
            </Typography>
            <Stack direction="row" spacing={1} mt={0.6}>
              <Chip
                label={razaNombre}
                size="small"
                sx={{ height: 20, fontSize: '11px', fontWeight: 600, bgcolor: THEME.primaryLight, color: THEME.primary, border: `1px solid ${THEME.primaryMid}` }}
              />
              <Chip
                icon={<CalendarToday sx={{ fontSize: '11px !important', color: `${THEME.textSecondary} !important` }} />}
                label={pacienteEdad || 'Edad no disponible'}
                size="small"
                sx={{ height: 20, fontSize: '11px', bgcolor: THEME.surfaceAlt, color: THEME.textSecondary, border: `1px solid ${THEME.border}` }}
              />
            </Stack>
          </Box>
        </Box>

        {/* Acciones cabecera */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Abrir Vademécum">
            <IconButton
              onClick={() => setIsVademecumOpen(true)}
              sx={{ color: THEME.textSecondary, '&:hover': { bgcolor: THEME.primaryLight, color: THEME.primary } }}
            >
              <LocalPharmacyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isMaximized ? 'Restaurar' : 'Maximizar'}>
            <IconButton
              onClick={() => setIsMaximized(!isMaximized)}
              sx={{ color: THEME.textSecondary, '&:hover': { bgcolor: THEME.surfaceAlt } }}
            >
              {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewReceta}
            disabled={isEditing}
            size="small"
            sx={{
              bgcolor: THEME.primary, color: '#fff', borderRadius: '9px',
              textTransform: 'none', fontWeight: 700, fontSize: '13px',
              px: 2, py: 0.8, boxShadow: 'none',
              '&:hover': { bgcolor: THEME.primaryMid, boxShadow: THEME.shadow },
              '&.Mui-disabled': { bgcolor: THEME.border, color: THEME.textMuted },
            }}
          >
            Nueva Receta
          </Button>
          <IconButton onClick={handleClose} sx={{ color: THEME.textMuted, '&:hover': { color: THEME.danger } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ── CUERPO PRINCIPAL: dos columnas ───────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', gap: 0 }}>

        {/* ── COLUMNA IZQUIERDA: Historial ─── */}
        <Box sx={{
          width: 240,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: THEME.surface,
          borderRight: `1px solid ${THEME.border}`,
          overflow: 'hidden',
        }}>
          {/* Header historial */}
          <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon sx={{ fontSize: 16, color: THEME.textSecondary }} />
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary }}>
                Historial
              </Typography>
              {recetas.length > 0 && (
                <Chip
                  label={recetas.length}
                  size="small"
                  sx={{ height: 18, fontSize: '10px', fontWeight: 700, bgcolor: THEME.primaryLight, color: THEME.primary, minWidth: 24 }}
                />
              )}
            </Box>
            <Tooltip title="Actualizar">
              <IconButton size="small" onClick={fetchRecetas} sx={{ color: THEME.textMuted, '&:hover': { color: THEME.primary } }}>
                <RefreshIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Lista de recetas */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {recetas.length > 0 ? (
              recetas.map((receta) => (
                <RecetaCard
                  key={receta.id_receta}
                  receta={receta}
                  selected={selectedReceta?.id_receta === receta.id_receta}
                  onClick={() => handleSelectReceta(receta)}
                  truncateText={truncateText}
                />
              ))
            ) : (
              <Box sx={{ textAlign: 'center', mt: 6, px: 2 }}>
                <AssignmentIcon sx={{ fontSize: 40, color: THEME.border, mb: 1 }} />
                <Typography sx={{ fontSize: '13px', color: THEME.textMuted }}>
                  Sin recetas registradas
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* ── COLUMNA DERECHA: Detalle ─── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {showDetail ? (
            <>
              {/* Contenido scrolleable */}
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                {/* ── CARD: Info general ── */}
                <Paper elevation={0} sx={{ border: `1px solid ${THEME.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: THEME.surface, borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MedicalIcon sx={{ fontSize: 15, color: THEME.primary }} />
                    <Typography sx={{ fontSize: '12px', fontWeight: 800, color: THEME.primary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Información General
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2.5, bgcolor: THEME.surface }}>
                    <InfoField label="Cliente" value={clienteNombre} icon={PersonIcon} />
                    <InfoField label="Veterinario" value={usuarioNombre} icon={PersonIcon} />

                    {/* Fecha */}
                    <InfoField label="Fecha" icon={CalendarToday} editing={isEditing} value={formData.fecha_receta ? dayjs(formData.fecha_receta).format('DD/MM/YYYY') : null}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                        <DatePicker
                          value={formData.fecha_receta ? dayjs(formData.fecha_receta) : null}
                          onChange={(v) => setFormData({ ...formData, fecha_receta: v ? v.format('YYYY-MM-DD') : '' })}
                          slotProps={{
                            textField: {
                              size: 'small', fullWidth: true, sx: { mt: 0.5 },
                              error: !!errors.fecha_receta, helperText: errors.fecha_receta,
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </InfoField>

                    {/* Peso */}
                    <InfoField label="Peso (Lb)" icon={ScaleIcon} editing={isEditing} value={formData.peso_receta}>
                      <TextField
                        size="small" fullWidth type="number"
                        value={formData.peso_receta}
                        onChange={(e) => setFormData({ ...formData, peso_receta: e.target.value })}
                        error={!!errors.peso_receta} helperText={errors.peso_receta}
                        sx={{ mt: 0.5 }}
                      />
                    </InfoField>

                    {/* Temperatura */}
                    <InfoField label="Temperatura (°C)" icon={ThermostatIcon} editing={isEditing} value={formData.temp_receta}>
                      <TextField
                        size="small" fullWidth type="number"
                        value={formData.temp_receta}
                        onChange={(e) => setFormData({ ...formData, temp_receta: e.target.value })}
                        error={!!errors.temp_receta} helperText={errors.temp_receta}
                        sx={{ mt: 0.5 }}
                      />
                    </InfoField>

                    {/* Actualizado */}
                    {formData.updatedAt && (
                      <InfoField
                        label="Actualizado"
                        value={new Date(formData.updatedAt).toLocaleString('es-ES', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', hour12: true,
                        })}
                      />
                    )}
                  </Box>
                </Paper>

                {/* ── CARD: Detalle de receta ── */}
                <Paper elevation={0} sx={{ border: `1px solid ${THEME.border}`, borderRadius: '14px', overflow: 'hidden' }}>
                  <Box sx={{
                    px: 2.5, py: 1.5,
                    bgcolor: THEME.surface,
                    borderBottom: `1px solid ${THEME.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AssignmentIcon sx={{ fontSize: 15, color: THEME.primary }} />
                      <Typography sx={{ fontSize: '12px', fontWeight: 800, color: THEME.primary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Detalle de la Receta
                      </Typography>
                    </Box>
                    <Tooltip title={tooltipFormato} placement="left" arrow componentsProps={{
                      tooltip: { sx: { bgcolor: THEME.textPrimary, maxWidth: 260, borderRadius: '10px', p: 1.5 } },
                      arrow: { sx: { color: THEME.textPrimary } },
                    }}>
                      <IconButton size="small" sx={{ color: THEME.textMuted, '&:hover': { color: THEME.primary } }}>
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Hoja de papel */}
                  <Box sx={{ bgcolor: '#f0f4f2', p: 2 }}>
                    <Box sx={{
                      width: '100%',
                      maxWidth: '215.9mm',
                      height: '420px',
                      mx: 'auto',
                      bgcolor: THEME.surface,
                      borderRadius: '8px',
                      boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
                      border: `1px solid ${THEME.border}`,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                    }}>
                      {isEditing ? (
                        <LineNumberedTextarea
                          value={formData.detalle_receta || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, detalle_receta: e.target.value }))}
                          placeholder="Ingrese indicaciones, medicamentos, dosis, vía, frecuencia, duración..."
                        />
                      ) : (
                        <Typography sx={{
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          flex: 1, overflowY: 'auto', p: '20px 24px',
                          fontSize: '13.5px', lineHeight: 1.75, color: THEME.textPrimary,
                          fontFamily: '"DM Mono", "Courier New", monospace',
                        }}>
                          {formData.detalle_receta || (
                            <span style={{ color: THEME.textMuted, fontStyle: 'italic', fontFamily: 'inherit' }}>
                              Sin detalles registrados para esta receta.
                            </span>
                          )}
                        </Typography>
                      )}

                      {/* Contador inferior */}
                      <Box sx={{
                        px: 2, py: 1,
                        borderTop: `1px solid ${THEME.border}`,
                        display: 'flex', gap: 2, alignItems: 'center',
                        bgcolor: THEME.surfaceAlt,
                      }}>
                        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
                          {countLines(formData.detalle_receta)} líneas
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: THEME.textMuted }}>
                          {countWords(formData.detalle_receta)} palabras
                        </Typography>
                        {countLines(formData.detalle_receta) > 58 && (
                          <Chip
                            label="¡Posible salto de página!"
                            size="small"
                            sx={{ height: 18, fontSize: '10px', bgcolor: THEME.accentLight, color: THEME.accent, fontWeight: 700 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Paper>

              </Box>

              {/* ── BARRA DE ACCIONES FIJA ── */}
              <ActionBar
                isEditing={isEditing}
                selectedReceta={selectedReceta}
                onEdit={() => setIsEditing(true)}
                onSave={handleEditReceta}
                onCancelEdit={() => setIsEditing(false)}
                onSaveNew={handleSaveReceta}
                onCancelNew={handleCancelNewReceta}
                onPrint={() => setTemplateDialogOpen(true)}
                onDelete={() => handleDeleteReceta(selectedReceta.id_receta)}
              />
            </>
          ) : (
            // Estado vacío
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 4 }}>
              <Box sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: THEME.primaryLight,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AssignmentIcon sx={{ fontSize: 32, color: THEME.primary }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: THEME.textPrimary, mb: 0.5 }}>
                  Selecciona una receta
                </Typography>
                <Typography sx={{ fontSize: '13px', color: THEME.textSecondary }}>
                  Elige una del historial o crea una nueva
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleNewReceta}
                sx={{
                  mt: 1, borderRadius: '10px', textTransform: 'none',
                  fontWeight: 600, fontSize: '13px',
                  borderColor: THEME.primary, color: THEME.primary,
                  '&:hover': { bgcolor: THEME.primaryLight },
                }}
              >
                Crear nueva receta
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* ── DIALOG PLANTILLAS ─────────────────────────────────────────── */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '15px', fontWeight: 700, color: THEME.textPrimary, pb: 1 }}>
          <PrintIcon sx={{ fontSize: 18, color: THEME.primary }} />
          Seleccionar Plantilla
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: THEME.border }}>
          <RadioGroup value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}>
            {plantillas.map((p) => (
              <FormControlLabel
                key={p.id} value={p.id} control={<Radio size="small" sx={{ color: THEME.primary, '&.Mui-checked': { color: THEME.primary } }} />}
                label={
                  <Box sx={{ py: 0.5 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: THEME.textPrimary }}>{p.nombre}</Typography>
                    <Typography sx={{ fontSize: '12px', color: THEME.textSecondary }}>{p.descripcion}</Typography>
                  </Box>
                }
                sx={{ mb: 0.5, borderRadius: '8px', '&:hover': { bgcolor: THEME.surfaceAlt } }}
              />
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
          <Button onClick={() => setTemplateDialogOpen(false)} size="small"
            sx={{ borderRadius: '8px', textTransform: 'none', color: THEME.textSecondary }}>
            Cancelar
          </Button>
          <Button
            onClick={() => { handlePrintReceta(selectedReceta?.id_receta, selectedTemplate); setTemplateDialogOpen(false); }}
            variant="contained" size="small"
            disabled={!selectedTemplate || !selectedReceta?.id_receta}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, bgcolor: THEME.primary, '&:hover': { bgcolor: THEME.primaryMid }, boxShadow: 'none' }}
          >
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── SNACKBAR ──────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled"
          sx={{ width: '100%', borderRadius: '10px', boxShadow: THEME.shadowMd }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── DRAWER VADEMECUM ──────────────────────────────────────────── */}
     {/* ── DRAWER VADEMECUM HOMOGENIZADO (CIERRE CON CLIC FUERA) ── */}
      <Modal
        open={isVademecumOpen}
        onClose={() => setIsVademecumOpen(false)} // 💡 Esto unifica el comportamiento detectando el clic afuera
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.2)' } // Sombreado sutil igual que en Consultas
          }
        }}
      >
        <Box sx={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          width: { xs: '100%', md: 800 },
          bgcolor: THEME.surface,
          boxShadow: `-8px 0 40px rgba(0,0,0,0.15)`,
          display: 'flex', flexDirection: 'column',
          borderLeft: `1px solid ${THEME.border}`,
          outline: 'none', // Evita el borde de enfoque automático por accesibilidad de MUI
        }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${THEME.border}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalPharmacyIcon sx={{ color: THEME.primary }} />
              <Typography sx={{ fontWeight: 700, color: THEME.textPrimary }}>Vademécum</Typography>
            </Box>
            <Button
              variant="contained" size="small" startIcon={<CloseIcon />}
              onClick={() => setIsVademecumOpen(false)}
              sx={{ bgcolor: THEME.danger, '&:hover': { bgcolor: '#b5373b' }, borderRadius: '8px', textTransform: 'none', boxShadow: 'none' }}
            >
              Cerrar
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Vademecum isDrawerContext={true} />
          </Box>
        </Box>
      </Modal>
      
    </Box>
  );
};

export default RecetasPacienteUI;